import { Modal } from "@dropbox/dig-components/modal";
import classNames from "classnames";
import { useCallback, useEffect, useRef, useState } from "react";
import { DocumentDetailsPanel } from "./DocumentDetailsPanel";
import { DocumentPreviewArea } from "./DocumentPreviewArea";
import { DocumentPreviewHeader } from "./DocumentPreviewHeader";
import styles from "./DocumentPreviewModal.module.css";

import type { DocumentFollowUpSuggestion } from "@mirage/mosaics/QuerySuggestions/useDocumentFollowUpSuggestions";
import type { ResultActionConfig } from "@mirage/search/SearchResults/ResultRow/types";
import type { SearchResult } from "@mirage/service-dbx-api";

type DocumentPreviewModalProps = {
  open: boolean;
  onClose: () => void;
  result: SearchResult;
  resultActionConfig: ResultActionConfig;
  onSuggestionClick: (suggestion: DocumentFollowUpSuggestion) => void;
};

export function DocumentPreviewModal({
  open,
  onClose,
  result,
  resultActionConfig,
  onSuggestionClick,
}: DocumentPreviewModalProps) {
  const [zoomLevel, setZoomLevel] = useState(100);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [currentPage, _] = useState(1);
  const [showMoreDetails, setShowMoreDetails] = useState(false);

  const [previewWidth, setPreviewWidth] = useState(60);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleZoomIn = () => {
    setZoomLevel((prev) => {
      const newZoom = Math.min(prev + 25, 200);
      return newZoom;
    });
  };

  const handleZoomOut = () => {
    setZoomLevel((prev) => {
      const newZoom = Math.max(prev - 25, 50);
      return newZoom;
    });
  };

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging || !containerRef.current) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const newWidth =
        ((e.clientX - containerRect.left) / containerRect.width) * 100;

      const constrainedWidth = Math.min(Math.max(newWidth, 45), 80);
      setPreviewWidth(constrainedWidth);
    },
    [isDragging],
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "col-resize";

      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        document.body.style.cursor = "";
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const detailsWidth = 100 - previewWidth;

  return (
    <Modal
      open={open}
      onRequestClose={onClose}
      shouldCloseOnOverlayClick={true}
      isCentered={true}
      shouldFocusAfterRender={true}
      className={styles.modalWindow}
      width="large"
    >
      <DocumentPreviewHeader
        result={result}
        onClose={onClose}
        resultActionConfig={resultActionConfig}
      />

      <Modal.Body className={styles.body}>
        <div
          className={classNames(styles.contentContainer, {
            [styles.dragging]: isDragging,
          })}
          ref={containerRef}
          style={
            {
              "--preview-width": `${previewWidth}%`,
              "--details-width": `${detailsWidth}%`,
            } as React.CSSProperties
          }
        >
          <DocumentPreviewArea
            result={result}
            zoomLevel={zoomLevel}
            currentPage={currentPage}
            onZoomIn={handleZoomIn}
            onZoomOut={handleZoomOut}
          />

          <button
            className={classNames(styles.resizeHandle, {
              [styles.dragging]: isDragging,
            })}
            onMouseDown={handleMouseDown}
            aria-label="Resize preview area"
            onKeyDown={(e) => {
              if (e.key === "ArrowLeft") {
                e.preventDefault();
                setPreviewWidth((prev) => Math.max(prev - 5, 45));
              } else if (e.key === "ArrowRight") {
                e.preventDefault();
                setPreviewWidth((prev) => Math.min(prev + 5, 80));
              }
            }}
          />

          <DocumentDetailsPanel
            result={result}
            showMoreDetails={showMoreDetails}
            onToggleDetails={() => setShowMoreDetails(!showMoreDetails)}
            onSuggestionClick={onSuggestionClick}
          />
        </div>
      </Modal.Body>
    </Modal>
  );
}
